import { Icon } from "@circleco/compass/components/Icon";
import {
  DragHandle,
  SortableList,
} from "@circleco/compass/components/SortableList";
import type { SortableBaseItem } from "@circleco/compass/components/SortableList";
import { useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import { flushSync } from "react-dom";

import type { FileUploadState } from "@/react/hooks/useParallelDirectUploads";
import { useIsVisibleOnDOM } from "@/react/hooks/utils/useIsVisibleOnDOM";

import { DeleteImageButton } from "../../CreatePost/Carousel/DeleteImageButton";
import { Scroller, scroll } from "../../Shared/Scroller";
import type { SelectedImage } from "../../store";
import { useImagePostModalStore } from "../../store";
import { useHandleFileSelection } from "../../UploadImages/hooks/useHandleFileSelection";
import {
  MAX_IMAGES,
  useMultiImageUpload,
} from "../../UploadImages/hooks/useMultiImageUpload";
import { useRemoveImage } from "../hooks/useRemoveImage";
import { LoadingThumbnail } from "./LoadingThumbnail";
import { ThumbnailImage } from "./ThumbnailImage";

type SortableSelectedImage = SelectedImage & SortableBaseItem;

interface ThumbnailsProps {
  currentImageIndex: number;
  editingImageSignedId?: string;
  fileStates: FileUploadState[];
  onDeleteAllImages: () => void;
  selectedFiles: SelectedImage[];
  setCurrentImageIndex: Dispatch<SetStateAction<number>>;
  status: "idle" | "uploading" | "completed" | "error";
}

export const Thumbnails = ({
  selectedFiles,
  setCurrentImageIndex,
  currentImageIndex,
  status,
  fileStates,
  editingImageSignedId,
  onDeleteAllImages,
}: ThumbnailsProps) => {
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [leftHookRef, isLeftEndVisible] = useIsVisibleOnDOM<HTMLDivElement>();
  const [rightHookRef, isRightEndVisible] = useIsVisibleOnDOM<HTMLDivElement>();
  const { uploadedImages, setSelectedFiles } = useImagePostModalStore();
  const { handleFileSelection } = useHandleFileSelection(() => {
    setTimeout(
      () =>
        scroll({
          direction: "right",
          scrollableContainerRef,
        }),
      200,
    );
  });
  const { getInputProps, open } = useMultiImageUpload({
    handleFileSelection,
    selectedFiles,
    uploadedFilesCount: uploadedImages.length,
  });
  const { removeImage } = useRemoveImage();
  const dndFiles: SortableSelectedImage[] = selectedFiles.map(image => ({
    ...image,
    id: image.preview,
  }));
  const currentImage = dndFiles[currentImageIndex];

  const canAddMoreImages =
    !editingImageSignedId &&
    selectedFiles.length + uploadedImages.length < MAX_IMAGES;

  const handleItemsChange = (images: SortableSelectedImage[]) => {
    const newIndex = images.findIndex(
      image => image.preview === currentImage?.preview,
    );

    if (newIndex >= 0 && newIndex !== currentImageIndex) {
      flushSync(() => {
        setCurrentImageIndex(newIndex);
      });
    }
    setSelectedFiles(
      images.map(image => ({
        crop: image.crop,
        croppedAreaPixels: image.croppedAreaPixels,
        file: image.file,
        preview: image.preview,
        zoom: image.zoom,
      })),
    );
  };

  return (
    <div className="relative mt-6 flex w-full min-w-0 overflow-hidden px-1">
      {!isLeftEndVisible && (
        <Scroller
          direction="left"
          scrollableContainerRef={scrollableContainerRef}
        />
      )}
      <div
        className="scrollbar-hide m-auto flex items-center overflow-x-scroll px-1 pt-1"
        ref={scrollableContainerRef}
        data-testid="thumbnails-list"
      >
        <div className="size-px" ref={leftHookRef} />
        {status === "uploading" || status === "completed" ? (
          selectedFiles.map((image, index) => (
            <LoadingThumbnail
              fileStates={fileStates}
              index={index}
              key={image.preview}
            />
          ))
        ) : (
          <SortableList<SortableSelectedImage>
            axis="horizontal"
            className="flex shrink-0 items-center"
            draggingClassName="opacity-60"
            items={dndFiles}
            mode="flat"
            onItemsChange={handleItemsChange}
            restrictToContainer
            renderItem={image => {
              const index = selectedFiles.findIndex(
                selectedImage => selectedImage.preview === image.preview,
              );
              const selectedFile = selectedFiles[index];

              if (!selectedFile) {
                return <div />;
              }

              return (
                <div
                  className="group relative shrink-0"
                  data-testid="thumbnail-item"
                >
                  <DeleteImageButton
                    onClick={() => {
                      removeImage(selectedFile);
                      if (selectedFiles.length === 1) {
                        onDeleteAllImages();
                      }
                    }}
                    index={index}
                    currentImageIndex={currentImageIndex}
                  />
                  <DragHandle
                    render={({ ref, style }) => (
                      <div
                        ref={ref}
                        style={style}
                        className="cursor-grab active:cursor-grabbing"
                      >
                        <ThumbnailImage
                          image={selectedFile}
                          index={index}
                          setCurrentImageIndex={setCurrentImageIndex}
                          currentImageIndex={currentImageIndex}
                        />
                      </div>
                    )}
                  />
                </div>
              );
            }}
          />
        )}
        {canAddMoreImages && (
          <button
            type="button"
            className="hover:bg-secondary text-default ml-1 flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-md border border-dashed"
            onClick={() => status !== "uploading" && open()}
          >
            <input {...getInputProps()} data-testid="upload_more_images" />
            <Icon name="plus" size="sm" />
          </button>
        )}
        <div className="size-px" ref={rightHookRef} />
      </div>
      {!isRightEndVisible && (
        <Scroller
          direction="right"
          scrollableContainerRef={scrollableContainerRef}
        />
      )}
    </div>
  );
};
